import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { SignaturePad } from "@/components/SignaturePad";
import { itemComplete, fmtDateTime } from "@/components/common";
import { Check, Paperclip, Info } from "lucide-react";
import { cn } from "@/lib/utils";

function PillRadio({ options, value, onChange, readonly, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid} role="radiogroup">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          role="radio"
          aria-checked={value === opt}
          disabled={readonly}
          onClick={() => onChange(opt)}
          data-testid={`${testid}-${opt.split(" ")[0].toLowerCase()}`}
          className={cn(
            "px-4 py-2 rounded-full text-sm font-medium border transition-colors min-h-[40px]",
            value === opt
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background hover:bg-muted border-border text-foreground"
          )}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

export function ChecklistItem({ item, p, readonly, onChange, onPreview }) {
  const t = item.input_type || "check";

  if (t === "info") {
    return (
      <div className="p-4 sm:px-5 bg-muted/30" data-testid={`checklist-item-${item.id}`}>
        {item.label && !["Standard & Resources", "Commitment", "Final Marker"].includes(item.label) && (
          <div className="flex items-center gap-2 mb-1">
            <Info size={14} className="text-accent" />
            <span className="font-heading font-semibold text-sm">{item.label}</span>
          </div>
        )}
        {item.body && <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{item.body}</p>}
        {item.description && <p className="text-xs text-accent mt-1 font-medium">{item.description}</p>}
      </div>
    );
  }

  if (t === "table") {
    return (
      <div className="p-4 sm:px-5" data-testid={`checklist-item-${item.id}`}>
        {item.label && <div className="font-heading font-semibold text-sm mb-2">{item.label}</div>}
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary text-primary-foreground">
                {item.columns?.map((c) => <th key={c} className="text-left px-3 py-2 font-medium">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {item.rows?.map((row, ri) => (
                <tr key={ri} className={ri % 2 ? "bg-muted/40" : ""}>
                  {row.map((cell, ci) => <td key={ci} className="px-3 py-2 align-top text-muted-foreground">{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const isCheck = t === "check" || t === "check_with_note";
  const complete = itemComplete(item, p);

  return (
    <div className="p-4 sm:px-5 flex gap-4" data-testid={`checklist-item-${item.id}`}>
      {isCheck && (
        <Checkbox
          checked={!!p.checked}
          disabled={readonly}
          onCheckedChange={(v) => onChange({ checked: !!v })}
          className="mt-0.5 h-6 w-6"
          data-testid={`checklist-item-checkbox-${item.id}`}
        />
      )}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-start gap-2 flex-wrap">
          <span className={cn("font-medium", complete && "text-foreground")}>{item.label}</span>
          {item.required && <span className="text-[10px] uppercase tracking-wide text-destructive/80 mt-1">Required</span>}
          {item.material_ids?.length > 0 && (
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => onPreview(item.material_ids[0])} data-testid={`item-material-${item.id}`}>
              <Paperclip size={14} />
            </Button>
          )}
        </div>
        {item.description && <p className="text-xs text-muted-foreground">{item.description}</p>}

        {t === "text" && (
          <Input disabled={readonly} value={p.value || ""} placeholder="Enter response…"
            onChange={(e) => onChange({ value: e.target.value })} data-testid={`item-text-${item.id}`} />
        )}
        {t === "number" && (
          <Input type="number" disabled={readonly} value={p.value || ""} placeholder="Enter number…" className="max-w-[180px]"
            onChange={(e) => onChange({ value: e.target.value })} data-testid={`item-number-${item.id}`} />
        )}
        {t === "date" && (
          <Input type="date" disabled={readonly} value={p.value || ""} className="max-w-[220px]"
            onChange={(e) => onChange({ value: e.target.value })} data-testid={`item-date-${item.id}`} />
        )}
        {t === "textarea" && (
          <Textarea disabled={readonly} value={p.value || ""} placeholder="Write specific evidence…" rows={2}
            onChange={(e) => onChange({ value: e.target.value })} data-testid={`item-textarea-${item.id}`} />
        )}
        {t === "radio" && (
          <PillRadio options={item.options || []} value={p.value || ""} readonly={readonly}
            onChange={(v) => onChange({ value: v })} testid={`item-radio-${item.id}`} />
        )}
        {t === "signature" && (
          <SignaturePad value={p.value || ""} readonly={readonly}
            onChange={(v) => onChange({ value: v })} testid={`item-signature-${item.id}`} />
        )}
        {t === "check_with_note" && (
          <Textarea disabled={readonly} value={p.note || ""} placeholder="Add a note…" rows={2}
            onChange={(e) => onChange({ note: e.target.value })} data-testid={`item-note-${item.id}`} />
        )}

        {p.completed_by_name && isCheck && (
          <div className="text-[11px] text-muted-foreground font-mono flex items-center gap-1">
            <Check size={11} className="text-success" /> {p.completed_by_name} · {fmtDateTime(p.completed_at)}
          </div>
        )}
      </div>
    </div>
  );
}
